import type { ComponentChildren } from 'preact'
import { useRef, useState } from 'preact/hooks'
import { AlertTriangle, CloudUpload, Film, Info, Loader2, RotateCcw, Upload } from 'lucide-preact'
import type { WebinarRoomRecordingViewDto } from '@/api/webinarRoom'
import { CLOUD_PROVIDERS, CLOUD_PROVIDER_LABEL, type CloudProvider } from '@/components/common/upload/cloudFile'
import type { DirectBlobUpload } from '@/components/common/upload/useDirectBlobUpload'
import { GoogleDriveIcon } from '@/components/common/icons/GoogleDriveIcon'
import { OneDriveIcon } from '@/components/common/icons/OneDriveIcon'
import { DropboxIcon } from '@/components/common/icons/DropboxIcon'
import {
    PREPARING_TOO_LONG, ROOM_RECORDING_ACCEPT, cloudSourceOf, leaveNote, preparingTooLong, transferText, type ReplacementView,
} from './roomRecording'
import { STALLED_NOTE, waitingText } from './roomUpload'
import { BUTTON, BUTTON_GHOST, LINK_BUTTON } from './roomStyles'

/** The Room tab's video upload pieces, shared by the Recording and While viewers wait cards. */

const PROVIDER_ICON: Record<CloudProvider, (props: { className?: string }) => ComponentChildren> = {
    GoogleDrive: GoogleDriveIcon,
    OneDrive: OneDriveIcon,
    Dropbox: DropboxIcon,
}

export function Progress({ fraction }: { fraction: number | null }) {
    return (
        <div className="mt-2 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden" aria-hidden="true">
            {fraction === null
                ? <span className="block h-full w-1/3 rounded-full bg-blue-500 animate-pulse" />
                : <span className="block h-full w-full rounded-full bg-blue-500 origin-left transition-transform duration-500" style={{ transform: `scaleX(${fraction})` }} />}
        </div>
    )
}

export function SourceIcon({ source, className }: { source: CloudProvider | null; className: string }) {
    if (!source) return <Film className={className} />
    const Icon = PROVIDER_ICON[source]
    return <Icon className={className} />
}

interface DropZoneProps {
    /** What is dropped, as the sentence names it: "recording", "new recording", "video". */
    what: string
    /** The second line: formats, size and any length limit. */
    limits: string
    disabled: boolean
    onFile: (file: File) => void
    onCloud: (provider: CloudProvider) => void
}

export function DropZone({ what, limits, disabled, onFile, onCloud }: DropZoneProps) {
    const [over, setOver] = useState(false)
    const input = useRef<HTMLInputElement>(null)
    return (
        <div
            className={`flex flex-col items-center text-center gap-1 px-6 pt-9 pb-8 rounded-xl border-[1.5px] border-dashed transition-colors ${over
                ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20'
                : 'border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/40'}`}
            onDragOver={event => { event.preventDefault(); if (!disabled) setOver(true) }}
            onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOver(false) }}
            onDrop={event => {
                event.preventDefault()
                setOver(false)
                const file = event.dataTransfer?.files?.[0]
                if (file && !disabled) onFile(file)
            }}
        >
            <span className="w-11 h-11 mb-2.5 rounded-full grid place-items-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-400">
                <CloudUpload className="w-5 h-5" />
            </span>
            <p className="text-[15px] font-medium text-slate-900 dark:text-white">
                Drag the {what} here, or{' '}
                <button type="button" className={LINK_BUTTON} disabled={disabled} onClick={() => input.current?.click()}>choose a file</button>
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-[52ch]">{limits}</p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2" role="group" aria-label="Import from cloud storage">
                <span className="mr-1 text-sm text-slate-500 dark:text-slate-400">Or import from</span>
                {CLOUD_PROVIDERS.map(provider => (
                    <button key={provider} type="button" disabled={disabled} onClick={() => onCloud(provider)} className={BUTTON}>
                        <SourceIcon source={provider} className="w-4 h-4" />
                        {CLOUD_PROVIDER_LABEL[provider]}
                    </button>
                ))}
            </div>
            {/* The drop zone's own picker; hidden, as the landing-page font picker's is, because the zone is the control. */}
            <input
                ref={input}
                type="file"
                accept={ROOM_RECORDING_ACCEPT}
                className="hidden"
                aria-hidden="true"
                tabIndex={-1}
                onChange={event => {
                    const target = event.currentTarget
                    const file = target.files?.[0]
                    target.value = ''
                    if (file) onFile(file)
                }}
            />
        </div>
    )
}

/** What a card's replacement strip says about the video in use, which stays until the replacement is ready. */
export interface ReplacementCopy {
    /** The failed strip's heading: "Couldn’t prepare new-cut.mov for streaming." */
    failedHeading: (fileName: string) => string
    /** Opens the failed strip's reason: "The current recording is still in use." */
    currentKept: string
    preparing: string
    /** Follows {@link PREPARING_TOO_LONG} under a replacement prepared longer than the stuck window. */
    stuck: string
    /** Follows where the upload runs, under a replacement on its way. */
    uploading: string
    /** The failed strip's button that drops the replacement. */
    keepCurrent: string
}

interface ReplacementStripProps {
    view: NonNullable<ReplacementView>
    pending: WebinarRoomRecordingViewDto | null
    /** This browser's upload of the replacement, while it runs. */
    upload: DirectBlobUpload | null
    copy: ReplacementCopy
    now: Date
    canEdit: boolean
    locked: boolean
    lockTitle: string | undefined
    busy: boolean
    /** Drops the replacement, from Cancel and from the failed strip's keep button. */
    onCancel: () => Promise<void>
    onRetry: (videoId: string) => Promise<void>
    /** Opens the card's chooser, after the failed replacement is dropped. */
    onUploadDifferent: () => void
}

const FAILED_FALLBACK = 'Try again, or upload the new file as an MP4 (H.264).'

/**
 * A replacement on its way beside the video in use, shared by the Recording and While viewers wait cards. Dropping a
 * failed one stays open after sign-off, as the API allows, because nothing the room plays changes.
 */
export function ReplacementStrip({ view, pending, upload, copy, now, canEdit, locked, lockTitle, busy, onCancel, onRetry, onUploadDifferent }: ReplacementStripProps) {
    const fileName = upload?.fileName ?? pending?.FileName ?? ''
    const cancelButton = canEdit && <button type="button" className={BUTTON} disabled={busy} onClick={() => void onCancel()}>Cancel</button>

    if (view === 'failed') {
        return (
            <div role="alert" className="mt-4 rounded-lg bg-slate-50 dark:bg-slate-900/40 px-4 py-3.5">
                <div className="flex items-start gap-3">
                    <span className="w-8 h-8 flex-none rounded-lg grid place-items-center border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400">
                        <AlertTriangle className="w-4 h-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900 dark:text-white break-words">{copy.failedHeading(fileName)}</p>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{copy.currentKept} {pending?.FailureMessage?.trim() || FAILED_FALLBACK}</p>
                    </div>
                </div>
                {canEdit && (
                    <div className="mt-3 ml-11 flex flex-wrap gap-2">
                        <button type="button" className={BUTTON} disabled={locked || busy} title={lockTitle} onClick={() => void onRetry(pending!.Id)}><RotateCcw className="w-3.5 h-3.5" />Try again</button>
                        <button type="button" className={BUTTON} disabled={locked || busy} title={lockTitle} onClick={async () => { await onCancel(); onUploadDifferent() }}>Upload a different file</button>
                        <button type="button" className={BUTTON_GHOST} disabled={busy} onClick={() => void onCancel()}>{copy.keepCurrent}</button>
                    </div>
                )}
            </div>
        )
    }

    if (view === 'preparing') {
        const stuck = preparingTooLong(pending, now)
        return (
            <div role="status" className="mt-4 rounded-lg bg-slate-50 dark:bg-slate-900/40 px-4 py-3.5 flex items-start gap-3">
                <span className="w-8 h-8 flex-none rounded-lg grid place-items-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-400">
                    {stuck ? <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" /> : <Loader2 className="w-4 h-4 animate-spin" />}
                </span>
                <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 dark:text-white break-words">Replacing with {fileName}</p>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{stuck ? `${PREPARING_TOO_LONG} ${copy.stuck}` : copy.preparing}</p>
                </div>
                {canEdit && (
                    <div className="flex flex-wrap justify-end gap-2">
                        {stuck && (
                            <button type="button" className={BUTTON} disabled={locked || busy} title={lockTitle} onClick={() => void onRetry(pending!.Id)}><RotateCcw className="w-3.5 h-3.5" />Try again</button>
                        )}
                        {cancelButton}
                    </div>
                )}
            </div>
        )
    }

    const source = upload ? null : cloudSourceOf(pending)
    const fraction = upload && upload.totalBytes > 0 ? upload.uploadedBytes / upload.totalBytes : null
    return (
        <div className="mt-4 rounded-lg bg-slate-50 dark:bg-slate-900/40 px-4 py-3.5">
            <div className="flex items-start gap-3">
                <span className="w-8 h-8 flex-none rounded-lg grid place-items-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-400">
                    {source ? <SourceIcon source={source} className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                </span>
                <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 dark:text-white break-words">
                        Replacing with {fileName}{fraction !== null && <>, <span className="tabular-nums">{Math.round(fraction * 100)}%</span></>}
                    </p>
                    <Progress fraction={fraction} />
                    <p className="mt-1 text-sm tabular-nums text-slate-500 dark:text-slate-400">
                        {upload ? transferText(upload.uploadedBytes, upload.totalBytes, upload.secondsLeft, false) : waitingText(pending, source)}
                    </p>
                </div>
                {cancelButton}
            </div>
            <p className="mt-2.5 ml-11 flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
                <Info className="w-3.5 h-3.5 mt-0.5 flex-none text-slate-400" />
                <span>{upload || source ? leaveNote(source) : STALLED_NOTE} {copy.uploading}</span>
            </p>
        </div>
    )
}
